import type {
  CopilotSpace,
  CopilotSpaceGitHubFileResource,
  CopilotSpaceId,
  CopilotSpaceResource,
} from '@github-ui/copilot-chat-types'
import {sendEvent} from '@github-ui/hydro-analytics'
import {repositoryTreePath} from '@github-ui/paths'
import {useLocation} from '@github-ui/react-router'
import {useMemo} from 'react'

import {COPILOT_SPACES_PATH} from './copilot-chat-helpers'

export const SLUG_ERROR_MESSAGE =
  'The name of this space is too close to another existing space. Please modify the name and try again.'
export const MCP_LINK = 'https://github.com/mcp/io.github.github/github-mcp-server'

// Regex patterns for matching copilot space paths
const SPACE_OWNER_PATTERN = '[a-zA-Z0-9-_]+'
const SPACE_ID_PATTERN = '[0-9]+'

/**
 * Returns a path for showing an individual space.
 */
export function getCopilotSpacePath(space: CopilotSpaceId) {
  const {id, owner} = space
  return `${COPILOT_SPACES_PATH}/${owner}/${id}`
}

/**
 * Returns if a path matches the Copilot Spaces list path.
 */
export function isCopilotSpacesListPath(path: string) {
  return path === COPILOT_SPACES_PATH || path === `${COPILOT_SPACES_PATH}/`
}

/**
 * Hook to keep track of whether the current path matches the Copilot Spaces list path.
 */
export function useIsCopilotSpacesListPath() {
  const location = useLocation()
  const path = location.pathname

  return useMemo(() => isCopilotSpacesListPath(path), [path])
}

/**
 * Returns true for /copilot/spaces/:owner/:id style path
 * @param allowEdit If true, also matches paths ending with /edit
 * @param path path to check
 * @returns true if the current path matches the defined space path formats
 */
export function isCopilotSpacePath(path: string, allowEdit = false) {
  // Base regex patterns
  const spaceOwnerPattern = `${COPILOT_SPACES_PATH}/${SPACE_OWNER_PATTERN}/${SPACE_ID_PATTERN}`
  const spaceOwnerPathRegex = new RegExp(`^${spaceOwnerPattern}${allowEdit ? '(/edit)?' : ''}$`)

  const isSpacePath = path.match(spaceOwnerPathRegex)

  return Boolean(isSpacePath)
}

/**
 * Hook to keep track of whether the current path matches an individual space.
 * @param location the react router location for when the ssrSafeLocation is undefined.
 * @param allowEdit if true, also matches paths ending with /edit
 * @returns true if the current path matches the spaces list or an individual space path.
 */
export function useIsCopilotSpacePath(allowEdit = false) {
  const location = useLocation()
  const path = location.pathname

  return useMemo(() => isCopilotSpacePath(path, allowEdit), [allowEdit, path])
}

/**
 * Hook to keep track of whether the current path matches the Spaces list page or an individual page.
 * @param location the react router location for when the ssrSafeLocation is undefined.
 * @returns true if the current path matches the spaces list or an individual space path.
 */
export function useIsCopilotSpacesPage() {
  const isListPage = useIsCopilotSpacesListPath()
  const isSpacePath = useIsCopilotSpacePath()
  return isListPage || isSpacePath
}

/**
 * Parses the space ID from the current URL and returns it.
 * We have to rely on the URL from the browser history because the space ID in the React Router state may be stale
 * since we manually push a history entry when creating a new thread and don't perform a navigation.
 */
export function useRouteSpaceId(): CopilotSpaceId | null {
  const location = useLocation()
  const pathname = location.pathname

  return useMemo(() => {
    // Match /copilot/spaces/:owner/:number format
    const ownerMatch = pathname.match(
      new RegExp(`${COPILOT_SPACES_PATH}/(${SPACE_OWNER_PATTERN})/(${SPACE_ID_PATTERN})`),
    )
    if (ownerMatch) {
      const [, owner, spaceId] = ownerMatch
      if (owner && spaceId) {
        const id = parseInt(spaceId, 10)
        if (!isNaN(id)) return {id, owner}
      }
    }

    return null
  }, [pathname])
}

export function customCopilotIdsAreEqual(a: CopilotSpaceId | null, b: CopilotSpaceId | null): boolean {
  if (a === b) return true
  if (a === null || b === null) return false
  return a.id === b.id && a.owner === b.owner
}

/**
 * Checks if a custom copilot matches the provided copilot ID.
 * @param customCopilot - The custom copilot to check
 * @param customCopilotId - The ID to match against
 * @returns True if the copilot matches the ID, false otherwise
 *
 * Returns true if both ID and owner match
 */
export function customCopilotMatchesId(
  customCopilot: Pick<CopilotSpace, 'id' | 'owner'>,
  customCopilotId: CopilotSpaceId,
): boolean {
  return customCopilot.id === customCopilotId.id && customCopilot.owner === customCopilotId.owner
}

export function findCustomCopilot<T extends Pick<CopilotSpace, 'id' | 'owner'>>(
  customCopilots?: T[],
  customCopilotId?: CopilotSpaceId | null,
): T | undefined {
  if (!customCopilotId) return undefined
  return customCopilots?.find(copilot => customCopilotMatchesId(copilot, customCopilotId))
}

/**
 * Generates the custom copilot API path using URL structure with owner/number format if an id is provided.
 * If CopilotSpaceId is null it returns a path appropriate for POST requests
 */
export function customCopilotApiPath(id?: CopilotSpaceId | null): string {
  if (!id) return COPILOT_SPACES_PATH
  return `${COPILOT_SPACES_PATH}/${id.owner}/${id.id}`
}

export function getRepositoryFromOrgSearchQuery(org: string, queryString: string, includeForks = false): string {
  const orgIndexSplit = queryString.indexOf('/')
  const includeForksString = includeForks ? 'fork:true' : ''
  let query: string

  // If an org is in the user-provided query (e.g. "myorg/")
  if (orgIndexSplit !== -1 && orgIndexSplit !== 0) {
    const orgNameFromQuery = queryString.slice(0, orgIndexSplit)
    // This is the second part of a query like "myorg/myrepo" where "myrepo" is the
    // repo name. This will be an empty string if the query is just "myorg/"
    const repoNameFromQuery = queryString.slice(orgIndexSplit + 1)

    if (org === orgNameFromQuery) {
      // If passed in org matches the org in the query, search for the org's repos.
      //
      // For example, if the query is "myorg/myrepo", it will search for "myrepo" in the "myorg" org.
      query = `org:${org} ${repoNameFromQuery}`
    } else {
      // If org in the user-provided query is not the passed in org, do a public repo search.
      //
      // For example, if the user-provided query is "anotherorg/myrepo" and the passed in org is
      // "myorg", it will search for public repos named "myrepo" in the org "anotherorg".
      query = `org:${orgNameFromQuery} ${repoNameFromQuery} visibility:public`
    }
  } else {
    // When no org is in the user-provided query use the passed in org and use the full user-provided query string.
    //
    // For example, if the user-provided query is "react" and the passed in org is "myorg",
    // it will search for "react" in the "myorg" org.
    query = `org:${org} ${queryString}`
  }

  // Build full query and replace multiple spaces with a single space
  return `${query} in:name archived:false ${includeForksString}`.trim().replace(/\s+/g, ' ')
}

/**
 * Checks if the custom copilot size percentage is greater than 100
 * @param customCopilot
 * @returns true if the custom copilot has exceeded the size limit
 */
export function spaceSizeExceeded(customCopilot: CopilotSpace | undefined): boolean {
  return (customCopilot?.sizePercentage ?? 0) > 100
}

/**
 * NOT USED at the moment
 * Creates an event handler that prevents event propagation.
 * Used for custom copilot components to avoid triggering default behaviors.
 * @param handler The original event handler function
 * @returns A wrapped handler that stops propagation before executing the original handler
 */
export const createPropagationStoppingHandler =
  <T extends Event>(handler: (e: T) => void) =>
  (e: T) => {
    e.stopPropagation()
    handler(e)
  }

export function generateGitHubFileMetadata(resource: CopilotSpaceGitHubFileResource) {
  const path = resource.filePath.split('/')
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const fileName = path.pop()!
  const filePath = [resource.nwo, ...path].join('/')
  const nwo = resource.nwo.split('/')
  const sha = resource.sha
  const fileUrl =
    resource.fileExists && resource.sha
      ? repositoryTreePath({
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          repo: {ownerLogin: nwo[0]!, name: nwo[1]!},
          commitish: resource.sha,
          action: 'blob',
          path: resource.filePath,
        })
      : undefined

  return {
    filePath,
    fileName,
    fileUrl,
    sha,
  }
}

export function isIssueOrPrResource(resource: CopilotSpaceResource) {
  return resource.type === 'github_issue' || resource.type === 'github_pull_request'
}

export function sendRefTelemetry(res: CopilotSpaceResource[], source: string) {
  const refTypesAndCount = res.reduce(
    (acc, resource) => {
      acc[resource.type] = (acc[resource.type] || 0) + 1
      return acc
    },
    {} as Record<string, number>,
  )
  const context = {...refTypesAndCount, source}
  sendEvent('copilot-spaces.resources-added', context)
}
